import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ReferenceValueSynonym } from "@/entities/all";

export default function EditSynonymModal({ isOpen, onClose, onSynonymUpdated, synonym, values }) {
  const [formData, setFormData] = useState({
    reference_value_id: '',
    synonym: '',
    synonym_type: 'Alternative Name',
    confidence_score: 0.9,
    source: 'User Input',
    is_approved: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (synonym) {
      setFormData({
        reference_value_id: synonym.reference_value_id || '',
        synonym: synonym.synonym || '',
        synonym_type: synonym.synonym_type || 'Alternative Name',
        confidence_score: synonym.confidence_score || 0.9,
        source: synonym.source || 'User Input',
        is_approved: synonym.is_approved !== undefined ? synonym.is_approved : true
      });
    }
  }, [synonym, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!synonym) return;
    setIsSubmitting(true);
    try {
      await ReferenceValueSynonym.update(synonym.id, formData);
      onSynonymUpdated();
    } catch (error) {
      console.error('Error updating synonym:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Synonym</DialogTitle>
          <DialogDescription>
            Update the synonym '{synonym?.synonym}'.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div>
            <Label htmlFor="edit_reference_value_id">Canonical Value</Label>
            <Select value={formData.reference_value_id} onValueChange={(value) => handleChange('reference_value_id', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select canonical value" />
              </SelectTrigger>
              <SelectContent>
                {values.map(value => (
                  <SelectItem key={value.id} value={value.id}>
                    {value.canonical_value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="edit_synonym">Synonym Value</Label>
            <Input
              id="edit_synonym"
              value={formData.synonym}
              onChange={(e) => handleChange('synonym', e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="edit_synonym_type">Synonym Type</Label>
            <Select value={formData.synonym_type} onValueChange={(value) => handleChange('synonym_type', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Abbreviation">Abbreviation</SelectItem>
                <SelectItem value="Alternative Name">Alternative Name</SelectItem>
                <SelectItem value="Legacy Value">Legacy Value</SelectItem>
                <SelectItem value="Common Misspelling">Common Misspelling</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit_confidence_score">Confidence Score</Label>
              <Input
                id="edit_confidence_score"
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={formData.confidence_score}
                onChange={(e) => handleChange('confidence_score', parseFloat(e.target.value))}
              />
            </div>
            <div>
              <Label htmlFor="edit_source">Source</Label>
              <Input
                id="edit_source"
                value={formData.source}
                onChange={(e) => handleChange('source', e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="edit_is_approved">Approved Status</Label>
              <p className="text-[0.8rem] text-muted-foreground">
                Whether this synonym is approved for use.
              </p>
            </div>
            <Switch
              id="edit_is_approved"
              checked={formData.is_approved}
              onCheckedChange={(checked) => handleChange('is_approved', checked)}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update Synonym'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
